import { useNavigate } from 'react-router-dom';

const ProcurementIntelligencePage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#0D0D0D]">
      {/* Back Button */}
      <button
        onClick={() => { navigate('/'); window.scrollTo(0, 0); }}
        data-testid="back-to-home"
        className="fixed top-6 left-6 z-50 flex items-center space-x-2 text-white hover:text-[#00E600] transition-colors"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
        </svg>
        <span className="font-semibold">Back to Home</span>
      </button>

      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-center justify-center px-6 pt-24 pb-16">
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1551288049-bebda4e38f71?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwzfHxidXNpbmVzcyUyMGFuYWx5dGljc3xlbnwwfHx8fDE3NjEwNDY3NjR8MA&ixlib=rb-4.1.0&q=85)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}></div>
        
        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 uppercase tracking-wide">
            Procurement <span className="text-[#00E600]">Intelligence</span>
          </h1>
          <p className="text-xl text-[#B3B3B3] leading-relaxed">
            Stop guessing. Start knowing. AI-powered procurement that turns your supply chain into a competitive advantage.
          </p>
        </div>
      </section>

      {/* Overview */}
      <section className="py-16 px-6 bg-[#1A1A1A]">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-white mb-8 uppercase tracking-wide">Overview</h2>
          <div className="space-y-6 text-lg text-[#B3B3B3] leading-relaxed">
            <p className="text-xl text-white font-semibold">
              Every day, enterprises hemorrhage millions through inefficient procurement. Redundant vendors. 
              Missed opportunities. Supply chain blind spots that competitors exploit.
            </p>
            <p>
              NYAXO's Procurement Intelligence doesn't just optimize—it revolutionizes. Our AI analyzes thousands 
              of data points across your supply chain, identifying cost-saving opportunities, predicting disruptions, 
              and negotiating terms that traditional procurement teams miss.
            </p>
            <p>
              We've helped enterprises reduce procurement costs by up to 18% while improving supplier reliability 
              and reducing risk. Not through luck—through intelligence.
            </p>
          </div>
        </div>
      </section>

      {/* What Our Platform Does */}
      <section className="py-16 px-6 bg-[#0D0D0D]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-white mb-4 uppercase tracking-wide">
              AI-Powered <span className="text-[#00E600]">Procurement Platform</span>
            </h2>
            <p className="text-[#B3B3B3] text-lg max-w-3xl mx-auto">
              Transform procurement from cost center to strategic advantage with intelligent automation
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-[#1A1A1A] border border-[#2F2F2F] rounded-lg p-6 hover:border-[#00E600] transition-all duration-300 group">
              <div className="w-12 h-12 bg-gradient-to-br from-[#00E600] to-[#36E0A8] rounded-lg flex items-center justify-center mb-4 group-hover:shadow-[0_0_20px_rgba(0,230,0,0.5)] transition-all duration-300">
                <svg className="w-6 h-6 text-[#0D0D0D]" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z" />
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clipRule="evenodd" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Cost Optimization AI</h3>
              <ul className="space-y-2 text-sm text-[#B3B3B3]">
                <li>• Spend pattern analysis & anomaly detection</li>
                <li>• Price benchmarking across 50M+ data points</li>
                <li>• Contract term optimization algorithms</li>
                <li>• Hidden savings identification (avg 18%)</li>
              </ul>
            </div>

            <div className="bg-[#1A1A1A] border border-[#2F2F2F] rounded-lg p-6 hover:border-[#00E600] transition-all duration-300 group">
              <div className="w-12 h-12 bg-gradient-to-br from-[#00E600] to-[#36E0A8] rounded-lg flex items-center justify-center mb-4 group-hover:shadow-[0_0_20px_rgba(0,230,0,0.5)] transition-all duration-300">
                <svg className="w-6 h-6 text-[#0D0D0D]" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
                  <path fillRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z" clipRule="evenodd" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Supplier Intelligence</h3>
              <ul className="space-y-2 text-sm text-[#B3B3B3]">
                <li>• Real-time performance scoring & ranking</li>
                <li>• Financial health & risk assessment</li>
                <li>• Alternative supplier recommendations</li>
                <li>• Geopolitical risk monitoring (150+ countries)</li>
              </ul>
            </div>

            <div className="bg-[#1A1A1A] border border-[#2F2F2F] rounded-lg p-6 hover:border-[#00E600] transition-all duration-300 group">
              <div className="w-12 h-12 bg-gradient-to-br from-[#00E600] to-[#36E0A8] rounded-lg flex items-center justify-center mb-4 group-hover:shadow-[0_0_20px_rgba(0,230,0,0.5)] transition-all duration-300">
                <svg className="w-6 h-6 text-[#0D0D0D]" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Predictive Analytics</h3>
              <ul className="space-y-2 text-sm text-[#B3B3B3]">
                <li>• Demand forecasting (95% accuracy)</li>
                <li>• Supply disruption prediction</li>
                <li>• Price trend analysis & alerts</li>
                <li>• Inventory optimization recommendations</li>
              </ul>
            </div>

            <div className="bg-[#1A1A1A] border border-[#2F2F2F] rounded-lg p-6 hover:border-[#00E600] transition-all duration-300 group">
              <div className="w-12 h-12 bg-gradient-to-br from-[#00E600] to-[#36E0A8] rounded-lg flex items-center justify-center mb-4 group-hover:shadow-[0_0_20px_rgba(0,230,0,0.5)] transition-all duration-300">
                <svg className="w-6 h-6 text-[#0D0D0D]" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Risk Management</h3>
              <ul className="space-y-2 text-sm text-[#B3B3B3]">
                <li>• Multi-tier supply chain visibility</li>
                <li>• Compliance & ESG monitoring</li>
                <li>• Concentration risk identification</li>
                <li>• Business continuity planning</li>
              </ul>
            </div>

            <div className="bg-[#1A1A1A] border border-[#2F2F2F] rounded-lg p-6 hover:border-[#00E600] transition-all duration-300 group">
              <div className="w-12 h-12 bg-gradient-to-br from-[#00E600] to-[#36E0A8] rounded-lg flex items-center justify-center mb-4 group-hover:shadow-[0_0_20px_rgba(0,230,0,0.5)] transition-all duration-300">
                <svg className="w-6 h-6 text-[#0D0D0D]" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Automated Workflows</h3>
              <ul className="space-y-2 text-sm text-[#B3B3B3]">
                <li>• RFP/RFQ generation & distribution</li>
                <li>• Bid comparison & scoring</li>
                <li>• Contract lifecycle management</li>
                <li>• Purchase order optimization</li>
              </ul>
            </div>

            <div className="bg-[#1A1A1A] border border-[#2F2F2F] rounded-lg p-6 hover:border-[#00E600] transition-all duration-300 group">
              <div className="w-12 h-12 bg-gradient-to-br from-[#00E600] to-[#36E0A8] rounded-lg flex items-center justify-center mb-4 group-hover:shadow-[0_0_20px_rgba(0,230,0,0.5)] transition-all duration-300">
                <svg className="w-6 h-6 text-[#0D0D0D]" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M3 3a1 1 0 000 2v8a2 2 0 002 2h2.586l-1.293 1.293a1 1 0 101.414 1.414L10 15.414l2.293 2.293a1 1 0 001.414-1.414L12.414 15H15a2 2 0 002-2V5a1 1 0 100-2H3zm11.707 4.707a1 1 0 00-1.414-1.414L10 9.586 8.707 8.293a1 1 0 00-1.414 0l-2 2a1 1 0 101.414 1.414L8 10.414l1.293 1.293a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Dashboard & Insights</h3>
              <ul className="space-y-2 text-sm text-[#B3B3B3]">
                <li>• Real-time spend visibility</li>
                <li>• Executive dashboards & KPIs</li>
                <li>• Category performance tracking</li>
                <li>• Savings opportunity pipeline</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Problems We Solve */}
      <section className="py-16 px-6 bg-[#1A1A1A]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-white mb-4 uppercase tracking-wide">
              Problems We <span className="text-[#00E600]">Solve</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-[#0D0D0D] border border-[#2F2F2F] rounded-lg p-8 hover:border-[#00E600] transition-all duration-300">
              <h3 className="text-xl font-bold text-white mb-4">Cost Leakage</h3>
              <p className="text-[#B3B3B3] mb-3">
                <span className="text-white font-semibold">Problem:</span> Overpaying by 15-30% due to poor visibility, maverick spending, and weak negotiation leverage.
              </p>
              <p className="text-[#00E600] font-semibold">
                → Solution: AI identifies $2M+ in savings opportunities across contracts, pricing, and vendor consolidation.
              </p>
            </div>

            <div className="bg-[#0D0D0D] border border-[#2F2F2F] rounded-lg p-8 hover:border-[#00E600] transition-all duration-300">
              <h3 className="text-xl font-bold text-white mb-4">Supply Chain Disruptions</h3>
              <p className="text-[#B3B3B3] mb-3">
                <span className="text-white font-semibold">Problem:</span> Blindsided by supplier failures, geopolitical events, and material shortages costing millions.
              </p>
              <p className="text-[#00E600] font-semibold">
                → Solution: Predictive alerts 60-90 days before disruptions, with alternative sourcing recommendations.
              </p>
            </div>

            <div className="bg-[#0D0D0D] border border-[#2F2F2F] rounded-lg p-8 hover:border-[#00E600] transition-all duration-300">
              <h3 className="text-xl font-bold text-white mb-4">Slow Procurement Cycles</h3>
              <p className="text-[#B3B3B3] mb-3">
                <span className="text-white font-semibold">Problem:</span> 90-120 day procurement cycles delay projects, frustrate stakeholders, and lose competitive edge.
              </p>
              <p className="text-[#00E600] font-semibold">
                → Solution: Automated workflows reduce cycle time by 40%, freeing teams for strategic work.
              </p>
            </div>

            <div className="bg-[#0D0D0D] border border-[#2F2F2F] rounded-lg p-8 hover:border-[#00E600] transition-all duration-300">
              <h3 className="text-xl font-bold text-white mb-4">Supplier Risk Exposure</h3>
              <p className="text-[#B3B3B3] mb-3">
                <span className="text-white font-semibold">Problem:</span> Single points of failure, concentration risk, and hidden dependencies threaten business continuity.
              </p>
              <p className="text-[#00E600] font-semibold">
                → Solution: Multi-tier visibility maps 1,000+ suppliers, scores risk, and suggests diversification strategies.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Industries */}
      <section className="py-16 px-6 bg-[#0D0D0D]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-white mb-4 uppercase tracking-wide">
              Industries We <span className="text-[#00E600]">Transform</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { name: 'Manufacturing', challenge: '$50M+ in annual procurement', benefit: '22% cost reduction, zero stockouts' },
              { name: 'Retail & CPG', challenge: 'Complex supplier networks', benefit: '18% savings, 30% faster sourcing' },
              { name: 'Healthcare', challenge: 'Regulatory compliance', benefit: 'FDA-compliant + 15% cost savings' },
              { name: 'Technology', challenge: 'Rapid scaling needs', benefit: '3x supplier capacity, 25% savings' },
              { name: 'Construction', challenge: 'Project-based procurement', benefit: '12% under budget, on-time delivery' },
              { name: 'Automotive', challenge: 'Just-in-time supply chains', benefit: 'Zero disruptions, 20% efficiency gain' }
            ].map((industry, idx) => (
              <div key={idx} className="bg-[#1A1A1A] border border-[#2F2F2F] rounded-lg p-6 hover:border-[#00E600] transition-all duration-300 group">
                <div className="w-10 h-10 bg-gradient-to-br from-[#00E600]/20 to-[#36E0A8]/20 rounded-lg flex items-center justify-center mb-4 border border-[#00E600]/30 group-hover:border-[#00E600] transition-all duration-300">
                  <div className="w-2 h-2 bg-[#00E600] rounded-full"></div>
                </div>
                <h3 className="text-lg font-bold text-white mb-2">{industry.name}</h3>
                <p className="text-sm text-[#B3B3B3] mb-2"><span className="text-white font-semibold">Challenge:</span> {industry.challenge}</p>
                <p className="text-sm text-[#00E600] font-semibold">→ {industry.benefit}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why NYAXO */}
      <section className="py-16 px-6 bg-[#1A1A1A]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-white mb-4 uppercase tracking-wide">
              Why <span className="text-[#00E600]">NYAXO</span> Wins
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-[#0D0D0D] border-l-4 border-[#00E600] rounded-lg p-8">
              <h3 className="text-2xl font-bold text-white mb-6">Market Differentiators</h3>
              <div className="space-y-4 text-[#B3B3B3]">
                <p><span className="text-[#00E600]">→</span> <span className="text-white font-semibold">50M+ Price Data Points:</span> Unmatched benchmarking across industries & geographies</p>
                <p><span className="text-[#00E600]">→</span> <span className="text-white font-semibold">Real-Time Intelligence:</span> Live supplier monitoring, not outdated reports</p>
                <p><span className="text-[#00E600]">→</span> <span className="text-white font-semibold">Multi-Tier Visibility:</span> See beyond Tier 1 to hidden dependencies</p>
                <p><span className="text-[#00E600]">→</span> <span className="text-white font-semibold">Industry Templates:</span> Pre-built for manufacturing, retail, healthcare, tech</p>
                <p><span className="text-[#00E600]">→</span> <span className="text-white font-semibold">Expert Consulting:</span> Strategic guidance, not just software</p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="bg-[#0D0D0D] border border-[#2F2F2F] rounded-lg p-6">
                <h4 className="text-xl font-bold text-white mb-2">Cost Savings</h4>
                <div className="text-4xl font-bold text-[#00E600] mb-2">18%</div>
                <p className="text-[#B3B3B3]">Average procurement spend reduction</p>
              </div>
              <div className="bg-[#0D0D0D] border border-[#2F2F2F] rounded-lg p-6">
                <h4 className="text-xl font-bold text-white mb-2">Cycle Time</h4>
                <div className="text-4xl font-bold text-[#00E600] mb-2">40%</div>
                <p className="text-[#B3B3B3]">Faster procurement processes</p>
              </div>
              <div className="bg-[#0D0D0D] border border-[#2F2F2F] rounded-lg p-6">
                <h4 className="text-xl font-bold text-white mb-2">Risk Prevention</h4>
                <div className="text-4xl font-bold text-[#00E600] mb-2">85%</div>
                <p className="text-[#B3B3B3]">Supply disruptions predicted & avoided</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Software Solutions */}
      <section className="py-16 px-6 bg-[#1A1A1A]">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-white mb-8 uppercase tracking-wide">
            Our <span className="text-[#00E600]">Software Solutions</span>
          </h2>
          <div className="bg-[#0D0D0D] border border-[#2F2F2F] rounded-lg p-8">
            <p className="text-lg text-[#B3B3B3] mb-6">
              NYAXO's procurement intelligence platform has been deployed across global supply chains, 
              processing billions in procurement spend with measurable ROI.
            </p>
            <div className="border-l-4 border-[#00E600] pl-6 py-4">
              <p className="text-white font-semibold mb-2">📦 Custom Software Portfolio</p>
              <p className="text-[#B3B3B3] italic">
                [Your specific procurement intelligence software and tools will be listed here]
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 px-6 bg-[#0D0D0D]">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-white mb-12 uppercase tracking-wide">Measurable Impact</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { title: 'Cost Reduction', stat: '18%', desc: 'Average procurement cost savings' },
              { title: 'Faster Cycles', stat: '40%', desc: 'Reduction in procurement cycle time' },
              { title: 'Risk Mitigation', stat: '85%', desc: 'Supply chain disruptions prevented' }
            ].map((benefit, index) => (
              <div key={index} className="text-center">
                <div className="text-5xl font-bold text-[#00E600] mb-2">{benefit.stat}</div>
                <div className="text-xl font-semibold text-white mb-2">{benefit.title}</div>
                <div className="text-[#B3B3B3]">{benefit.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-6 bg-[#1A1A1A]">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-white mb-6">Transform Your Procurement</h2>
          <p className="text-lg text-[#B3B3B3] mb-8">
            Stop leaving money on the table. Let NYAXO's AI optimize your supply chain.
          </p>
          <button
            onClick={() => navigate('/#contact')}
            className="px-8 py-4 bg-gradient-to-r from-[#00E600] to-[#36E0A8] text-[#0D0D0D] font-bold rounded-md hover:shadow-[0_0_30px_rgba(0,230,0,0.6)] transition-all duration-300 uppercase tracking-wide"
          >
            Request a Demo
          </button>
        </div>
      </section>
    </div>
  );
};

export default ProcurementIntelligencePage;